import React, { useEffect, useState } from "react";
import { createRoot } from "react-dom/client";
import styled from "styled-components";
import { Autoplay, Lazy, Navigation, Pagination } from "swiper";
import { SwiperSlide } from "swiper/react";
import { apiConfiguration } from "../../api-configuration";
import { hookDefaultGATracking } from "../../features/ga-tracking";
import { FacebookApi, FacebookPostDto, ObjectTypes } from "../../lib-api";
import { ellipsize } from "../../util/helpers";
import { WrappedSwiper } from "./wrapped-swiper";

const Label = styled("span")`
  display: inline-block;
  background-color: #d03822;
  color: #fcfcfc;
  font-size: 12px;
  font-weight: 500;
  line-height: 18px;
  text-transform: uppercase;
  padding: 4px 6px;
  letter-spacing: 0.03rem;
  text-overflow: ellipsis;
  overflow: hidden;
  z-index: 1;
`;

const facebookApi = new FacebookApi(apiConfiguration);

type Posts = FacebookPostDto[] | undefined;

const FacebookCarousel: React.FC<{
  businessCategoryIds?: string[];
  container?: string;
  breakpointsOverride?: {
    [key: string]: {
      slidesPerView: number;
    };
  };
}> = ({ businessCategoryIds, container, breakpointsOverride }) => {
  const [posts, setPosts] = useState<Posts>(undefined);

  useEffect(() => {
    (async () => {
      try {
        const posts = await facebookApi.getFacebookPosts({
          getFacebookPostsForm: {
            businessCategoryIds,
          },
        });
        setPosts(posts);
      } catch (e) {
        console.error(e);
      }
    })();
  }, []);

  // Hook up GA tracking for dynamically rendered content
  useEffect(() => {
    if (posts && posts.length > 0) {
      // Use setTimeout to ensure DOM elements are rendered
      setTimeout(() => {
        const carouselElement = document.querySelector(
          "[data-facebook-carousel]"
        );
        if (carouselElement) {
          hookDefaultGATracking(carouselElement);
        }
      }, 100);
    }
  }, [posts]);

  if (posts !== undefined && posts.length < 3) {
    return null;
  }

  return (
    <div data-facebook-carousel>
      <Label className="my-3">Opslag fra vores lokale erhverv</Label>

      {posts !== undefined && posts.length > 0 && (
        <WrappedSwiper
          modules={[Navigation, Pagination, Lazy, Autoplay]}
          slidesPerView={1}
          breakpoints={
            breakpointsOverride
              ? breakpointsOverride
              : {
                  "640": {
                    slidesPerView: 2,
                  },
                  "768": {
                    slidesPerView: 2,
                  },
                  "1024": {
                    slidesPerView: 3,
                  },
                }
          }
          navigation
          pagination={{ clickable: true }}
          loop={true}
          preloadImages={false}
          lazy={{ enabled: true, checkInView: false, loadPrevNext: true }}
          autoplay={{ delay: 2500, disableOnInteraction: false }}
        >
          <>
            {posts.map((post, index) => (
              <SwiperSlide
                key={`${post.permalink}-${index}`}
                ga-track-impression=""
                data-itemid={post.id}
                data-itemtype={ObjectTypes.FacebookPost}
                data-container={container}
                data-businessid={post.businessId}
                data-businessname={post.businessName}
                data-customerid={post.customerId}
                data-customername={post.customerName}
              >
                <a
                  href={post.permalink ?? ""}
                  target="_blank"
                  rel="noopener nofollow noreferrer"
                  data-businessid={post.businessId}
                  data-businessname={post.businessName}
                  data-customerid={post.customerId}
                  data-customername={post.customerName}
                >
                  <div>
                    <img
                      alt="opslagsbillede"
                      data-src={post.imageUrl}
                      className="swiper-lazy"
                    />
                    <div className="swiper-lazy-preloader swiper-lazy-preloader-black"></div>
                  </div>
                  <div className="mx-2 d-flex flex-column">
                    <b className="mt-2">{post.businessName}</b>
                    <p className="pb-0 flex-grow-1">
                      {ellipsize(post.message ?? "")}
                    </p>
                    <small className="text-primary">Åbn opslaget</small>
                  </div>
                </a>
              </SwiperSlide>
            ))}
          </>
        </WrappedSwiper>
      )}
      <small className="d-block text-right">Annoncørbetalt indhold</small>
    </div>
  );
};

const facebokCarousels = document.getElementsByTagName("facebook-carousel");

for (let i = 0; i < facebokCarousels.length; i++) {
  const element = facebokCarousels.item(i) as HTMLElement;
  const root = createRoot(element);

  const businessCategoryIds = element.dataset.businessCategoryIds
    ? JSON.parse(element.dataset.businessCategoryIds)
    : null;

  const breakpointsOverride = element.dataset.breakpoints
    ? JSON.parse(element.dataset.breakpoints)
    : null;

  const container = element.dataset.container;

  root.render(
    <FacebookCarousel
      businessCategoryIds={businessCategoryIds}
      container={container}
      breakpointsOverride={breakpointsOverride}
    />
  );
}
